import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MainApiService from "../../../../../services/mainApi.service";
import { AutoCompleteUncontrolled, ISelectType, Table } from "@4uhub/lib4uhub";
import { Chip, Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { ITicketAutoTransferRules } from "../../../../../models/ticket-auto-transfer-rule";
import { useMemo, useState } from "react";

const TICKET_AUTO_TRANSFER_RULE_ROUTE =
  process.env.REACT_APP_TICKETS + "/api/v1/TicketAutoTransferRule/";
const ticketAutoTransferRuleService =
  new MainApiService<ITicketAutoTransferRules>(TICKET_AUTO_TRANSFER_RULE_ROUTE);

const TICKET_CHANNEL_ROUTE =
  process.env.REACT_APP_TICKETS + "/api/v1/TicketChannels/";
const ticketChannelService = new MainApiService<ISelectType>(
  TICKET_CHANNEL_ROUTE
);

const translationPath = "page.settings.crm.ticketAutoTransferRules.";

const TicketAutoTransferRulesPage = () => {
  const navigate = useNavigate();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { eI } = useParams();

  const [selectedTicketChannel, setSelectedTicketChannel] =
    useState<ISelectType | null>(null);

  const columns: GridColDef<ITicketAutoTransferRules>[] = [
    {
      field: "ticketChannel",
      headerName: t(translationPath + "ticket_channel") || "Board",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <Typography variant="body2" title={params.value.name}>
            {params.value.name}
          </Typography>
        );
      },
    },
    {
      field: "ticketChannelStep",
      headerName: t(translationPath + "ticket_channel_step") || "Board List",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <Typography variant="body2" title={params.value.name}>
            {params.value.name}
          </Typography>
        );
      },
    },
    {
      field: "ticketChannelTransfer",
      headerName: t(translationPath + "ticket_channel_transfer") || "Board",
      renderCell: ({
        row: {
          ticketChannelStepTransfer: { ticketChannel },
        },
      }) => {
        if (!ticketChannel.name) return "-";
        return (
          <Typography variant="body2" title={ticketChannel.name}>
            {ticketChannel.name}
          </Typography>
        );
      },
    },
    {
      field: "ticketChannelStepTransfer",
      headerName:
        t(translationPath + "ticket_channel_step_transfer") ||
        "Board list for transfer",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <Typography variant="body2" title={params.value.name}>
            {params.value.name}
          </Typography>
        );
      },
    },
    {
      field: "minutes",
      headerName: t(translationPath + "minutes") || "Minutes",
      maxWidth: 100,
    },
    {
      field: "initialDate",
      headerName: t(translationPath + "initial_date") || "Initial date",
      maxWidth: 150,
      renderCell: (params) => {
        const date = new Date(params.value);
        return params.value
          ? format(date, "P", {
              locale: language === "pt-BR" ? ptBR : enUS,
            })
          : "-";
      },
    },
    {
      field: "endDate",
      headerName: t(translationPath + "end_date") || "End date",
      maxWidth: 150,
      renderCell: (params) => {
        const date = new Date(params.value);
        return params.value
          ? format(date, "P", {
              locale: language === "pt-BR" ? ptBR : enUS,
            })
          : "-";
      },
    },
    {
      field: "transfer",
      headerName: t(translationPath + "transfer_type") || "Transfer Type",
      maxWidth: 170,
      renderCell: ({ row: { transfer, endOfDay } }) => {
        const label = transfer
          ? t(translationPath + "transfer_by_time")
          : endOfDay
          ? t(translationPath + "transfer_end_of_day")
          : t(translationPath + "transfer_not_selected");
        return (
          <Typography variant="body2" title={label}>
            {label}
          </Typography>
        );
      },
    },
    {
      field: "enable",
      headerName: t(translationPath + "enable") || "Active",
      maxWidth: 70,
      renderCell: ({ row: { transfer, endOfDay } }) => {
        const enabled = transfer || endOfDay;
        return (
          <Chip
            size="small"
            label={
              enabled
                ? t(translationPath + "active")
                : t(translationPath + "inactive")
            }
            color={enabled ? "success" : "error"}
          />
        );
      },
    },
  ];

  const handleAdd = () => {
    navigate("new");
  };

  const handleRowClick = (params: GridRowParams<ITicketAutoTransferRules>) => {
    navigate(`${params.id.toString()}/edit`);
  };

  const props = useMemo(() => {
    return {
      TicketChannelId: selectedTicketChannel
        ? selectedTicketChannel.id
        : undefined,
    };
  }, [selectedTicketChannel]);
  return (
    <Table<ITicketAutoTransferRules>
      showDefaultMacroFilters={false}
      service={ticketAutoTransferRuleService}
      columns={columns}
      title={t(translationPath + "ticket_auto_transfer_rules")}
      searchInput
      onAdd={handleAdd}
      onRowClick={handleRowClick}
      backButton
      serviceProps={props}
      macroFilters={() => (
        <Stack width={300}>
          <AutoCompleteUncontrolled
            label={t(translationPath + "ticket_channel") || "Ticket Channel"}
            onValueSelected={setSelectedTicketChannel}
            value={selectedTicketChannel}
            size="small"
            fullWidth
            service={ticketChannelService.list}
          />
        </Stack>
      )}
      backButtonUrl={`/e/${eI}/dashboard/settings`}
      searchInputPlaceHolder={
        t(translationPath + "search") ||
        "Search for a ticket auto transfer rule"
      }
      addButtonLabel={
        t(translationPath + "add_ticket_auto_transfer_rule") ||
        "Add Ticket Service Area"
      }
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default TicketAutoTransferRulesPage;
